import { type RefObject, useEffect, useState } from 'react';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import { averageProgress, TASK_SENT_EVENT } from '../core/_utils';

const SIZE = 35;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const HINT_MS = 3_500;
const CHIP_SIZE = 45;

/** Chip que voa de onde a exportação foi enviada até o sino. Sem CSS de componente: classes do tema + WAAPI. */
const flyChip = (from: DOMRect, anchor: Element) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const to = anchor.getBoundingClientRect();
  const chip = document.createElement('div');
  chip.dataset.taskChip = '';
  chip.className =
    'position-fixed d-flex flex-center w-45px h-45px rounded-circle bg-primary text-inverse-primary fs-2 shadow pe-none';
  chip.innerHTML = '<i class="bi bi-cloud-arrow-down-fill text-inverse-primary"></i>';
  chip.style.zIndex = '2000';
  chip.style.left = `${from.left + from.width / 2 - CHIP_SIZE / 2}px`;
  chip.style.top = `${from.top + from.height / 2 - CHIP_SIZE / 2}px`;
  document.body.appendChild(chip);

  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  chip.animate(
    [
      { transform: 'translate(0, 0) scale(0.4)', opacity: 0 },
      { transform: 'translate(0, -24px) scale(1.15)', opacity: 1, offset: 0.18 },
      { transform: `translate(${dx * 0.55}px, ${dy * 0.55 - 90}px) scale(0.95)`, opacity: 1, offset: 0.6 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.25)`, opacity: 0.4 },
    ],
    { duration: 900, easing: 'cubic-bezier(0.5, 0, 0.2, 1)' },
  ).onfinish = () => chip.remove();
};

type Props = {
  running: TaskInfoDto<string>[];
  /** Finalizadas desde a última vez que a aba Tarefas foi aberta. */
  unseen: number;
  /** O menu do sino está aberto: o balão de confirmação não disputa espaço com ele. */
  isOpen: boolean;
  /** O wrapper do sino, alvo do chip. */
  anchorRef?: RefObject<HTMLElement | null>;
};

/**
 * Sobreposto ao sino (irmão do botão, dentro do wrapper relativo): anel e contador enquanto há tarefa rodando,
 * contador verde com as finalizadas não vistas e o balão que confirma o envio para segundo plano.
 */
const TaskBellIndicator = ({ running, unseen, isOpen, anchorRef }: Props) => {
  const [hint, setHint] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const handleSent = (event: Event) => {
      // O balão nasce junto do envio, não do fim da animação.
      setHint(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setHint(false), HINT_MS);
      const from = (event as CustomEvent<DOMRect | undefined>).detail;
      if (from && anchorRef?.current) flyChip(from, anchorRef.current);
    };
    window.addEventListener(TASK_SENT_EVENT, handleSent);
    return () => {
      window.removeEventListener(TASK_SENT_EVENT, handleSent);
      clearTimeout(timeout);
    };
  }, [anchorRef]);

  const progress = averageProgress(running);

  return (
    <>
      {running.length > 0 ? (
        <>
          <svg
            aria-label={running.length === 1 ? '1 tarefa em andamento' : `${running.length} tarefas em andamento`}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={Math.round(progress * 100)}
            className="position-absolute top-0 start-0 pe-none"
            height={SIZE}
            role="progressbar"
            width={SIZE}
          >
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              fill="none"
              r={RADIUS}
              stroke="var(--bs-gray-300)"
              strokeWidth={STROKE}
            />
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              fill="none"
              r={RADIUS}
              stroke="var(--bs-primary)"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
              strokeLinecap="round"
              strokeWidth={STROKE}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            />
          </svg>
          {/* O animate.css anima `transform`: o posicionamento fica no span de fora para não ser sobrescrito. */}
          <span className="pe-none position-absolute top-0 start-100 translate-middle">
            <span className="badge badge-circle badge-primary w-15px h-15px fs-9 animate__animated animate__pulse animate__infinite">
              {running.length}
              <span className="visually-hidden"> em andamento</span>
            </span>
          </span>
        </>
      ) : null}
      {running.length === 0 && unseen > 0 ? (
        <span className="pe-none position-absolute top-0 start-100 translate-middle">
          <span className="badge badge-circle badge-success w-15px h-15px fs-9 animate__animated animate__bounceIn">
            {unseen}
            <span className="visually-hidden"> {unseen === 1 ? 'tarefa finalizada' : 'tarefas finalizadas'}</span>
          </span>
        </span>
      ) : null}
      {hint && !isOpen ? (
        <div
          className="position-absolute top-100 end-0 mt-3 w-250px bg-primary text-white rounded shadow px-4 py-3 animate__animated animate__fadeIn animate__faster"
          role="status"
          style={{ zIndex: 107 }}
        >
          <div className="fw-bold fs-7">Exportando em segundo plano</div>
          <div className="fs-8 opacity-75">Avisamos aqui, na aba Tarefas.</div>
        </div>
      ) : null}
    </>
  );
};

export default TaskBellIndicator;
